/* eslint-disable @angular-eslint/no-output-native */
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocomplete, MatAutocompleteDefaultOptions, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_CHIPS_DEFAULT_OPTIONS, MatChipGrid, MatChipInput, MatChipRemove, MatChipRow, MatChipsDefaultOptions } from '@angular/material/chips';
import { MAT_RIPPLE_GLOBAL_OPTIONS, MatOption, RippleGlobalOptions } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatError, MatFormField, MatFormFieldDefaultOptions, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { DynamicFormOption } from '@ng-dynamic-forms/core';
import { WINDOW } from 'ngx-window-token';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { ChipChoiceAutocompleteModel } from './chip-choice-autocomplete.model';

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-chip-choice-autocomplete',
  templateUrl: 'chip-choice-autocomplete.component.html',
  styleUrls:   ['chip-choice-autocomplete.component.scss'],
  standalone:  true,
  imports:     [
    NgIf,
    MatFormField,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatLabel,
    MatChipGrid,
    NgFor,
    MatChipRow,
    MatIcon,
    MatChipRemove,
    MatInput,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocomplete,
    MatOption,
    MatHint,
    MatError
  ]
})
export class ChipChoiceAutocompleteComponent extends CustomDynamicFormControlComponent<ChipChoiceAutocompleteModel<any>> implements OnInit {

  AUTOCOMPLETE_OPTIONS = inject<MatAutocompleteDefaultOptions>(MAT_AUTOCOMPLETE_DEFAULT_OPTIONS);
  CHIPS_OPTIONS = inject<MatChipsDefaultOptions>(MAT_CHIPS_DEFAULT_OPTIONS);
  FORM_FIELD_OPTIONS = inject<MatFormFieldDefaultOptions>(MAT_FORM_FIELD_DEFAULT_OPTIONS, {optional: true});
  RIPPLE_OPTIONS = inject<RippleGlobalOptions>(MAT_RIPPLE_GLOBAL_OPTIONS, {optional: true});
  private window = inject<Window>(WINDOW);

  @ViewChild('inputElement', {static: false}) inputElement: ElementRef;
  @ViewChild(MatAutocompleteTrigger) trigger;
  @ViewChild('chipList') chipList: MatChipGrid;

  options: DynamicFormOption<any>[];

  private _isValueUpdating = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    // any component setup that requires the model goes here
    this.options = this.model.options;
    this.onValueChange();
  }

  protected onValueChange() {
    if (!this._isValueUpdating) {
      this._isValueUpdating = true;

      if (!this.model.value) {
        this.model.value = [];
      }

      this._applyOptionsFilter();
      this._emitChange();

      this._isValueUpdating = false;
    }
  }

  onChipSelected(option: DynamicFormOption<any>): void {
    if (!this._modelContainsOption(option)) {

      this.model.value = [
        ...this.model.value,
        option
      ];
    }
    this.inputElement.nativeElement.value = '';
    this._focusAndShowAutocomplete();
  }

  onKeyUp(value: string) {
    if (typeof value === 'string') {
      this._applyOptionsFilter(value);
    }
  }

  onChipRemoved(val: DynamicFormOption<any>): void {
    this.model.value = this.model.value.filter(item => item !== val);
    this._focusAndShowAutocomplete();
  }

  private _setErrors() {
    const errors: ValidationErrors = this.model.required && (!this.model.value || this.model.value.length <= 0) ? {required: true} : null;
    this.group.get(this.model.id)
      .setErrors(errors);
    if (this.chipList) {
      this.chipList.errorState = !!errors;
    }
  }

  onInputBlur() {
    this._setErrors();
    this.inputElement.nativeElement.value = '';
  }

  getLabelForValue(val: any) {
    const matchingOption = this.model.options.find(option => this.model.compareWithFn(option.value, val));
    return matchingOption?.label;
  }

  private _applyOptionsFilter(inputValue?: string) {
    this.options = this.model.options.filter(
      (option: DynamicFormOption<any>) =>
        // only show values that have a partial string match and that have not yet been selected
        (!inputValue || option.label.toLowerCase()
          .includes(inputValue.toLowerCase())) && !this._modelContainsOption(option)
    );
  }

  private _modelContainsOption(option: DynamicFormOption<any>): boolean {
    return this.model.value.some(val => this.model.compareWithFn(option.value, val));
  }

  private _emitChange() {
    this._setErrors();
    this.change.emit(this.model.value);
  }

  private _focusAndShowAutocomplete() {
    this.window.setTimeout(() => {
      this.inputElement.nativeElement.focus();
      this.showAutocomplete();
    }, 1);
  }

  showAutocomplete() {
    this.trigger._onChange('');
    this.trigger.openPanel();
  }

  get placeholder(): string {
    // only show placeholder if value is empty and field is enabled
    return (!this.model.value?.length && !this.model.disabled && this.model.placeholder) || '';
  }
}
